import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { LineChart, Line, ResponsiveContainer, Tooltip as RTooltip } from "recharts";
import {
    ArrowLeft, Share2, Copy, Gift, Check, Mail, MailX, BarChart3, Crown, Send, Inbox,
} from "lucide-react";
import { API, getMyCardOpens } from "../lib/api";
import { canonicalShareUrl } from "../lib/shareUrl";
import { useAuth } from "../lib/AuthContext";
import ThemeToggle from "../components/ThemeToggle";
import AuthButton from "../components/AuthButton";
import SignatureBackdrop from "../components/SignatureBackdrop";
import CreatorStudio from "../components/CreatorStudio";
import { Toaster } from "../components/ui/sonner";

const PACK_LABELS = {
    cinematic: "Cinematic",
    vintage: "Vintage",
    brutalist: "Brutalist",
    minimal: "Minimal",
    unknown: "Other",
};
const PACK_COLORS = {
    cinematic: "#9FE7D9",
    vintage:   "#F2B49B",
    brutalist: "#FF5A1F",
    minimal:   "#E5E5EA",
    unknown:   "#8A857E",
};

/**
 * Human-friendly elapsed time: "just now", "2 min ago", "3 hr ago", "yesterday",
 * "3 days ago". Returns ISO date for older items so the dashboard doesn't lie
 * about a card opened 4 months ago looking "recent".
 */
function _timeAgo(iso) {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return "";
    const diffSec = Math.floor((Date.now() - then) / 1000);
    if (diffSec < 60) return "just now";
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} hr ago`;
    if (diffSec < 172800) return "yesterday";
    if (diffSec < 604800) return `${Math.floor(diffSec / 86400)} days ago`;
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function Bar({ label, count, max, color }) {
    const pct = max ? Math.max(4, (count / max) * 100) : 0;
    return (
        <div className="flex items-center gap-3" data-testid={`bar-${label.toLowerCase()}`}>
            <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-app-3 w-20 shrink-0">{label}</span>
            <div className="relative flex-1 h-2 rounded-full bg-surface-2 overflow-hidden">
                <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="absolute inset-y-0 left-0"
                    style={{ backgroundColor: color }}
                />
            </div>
            <span className="font-sans text-xs text-app w-6 text-right">{count}</span>
        </div>
    );
}

export default function Dashboard() {
    const navigate = useNavigate();
    const { user, loading, isPremium } = useAuth();
    const [my, setMy] = useState(null);
    const [admin, setAdmin] = useState(null);
    const [spiceStats, setSpiceStats] = useState(null);
    const [emailStatus, setEmailStatus] = useState(null);
    const [prefs, setPrefs] = useState(null);
    const [sendingPulse, setSendingPulse] = useState(false);
    const [copied, setCopied] = useState(false);
    const [opens, setOpens] = useState(null);  // { items: [...] } recent open events

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/", { replace: true }); return; }

        axios.get(`${API}/referrals/analytics`, { withCredentials: true })
            .then(({ data }) => setMy(data))
            .catch(() => setMy(null));

        axios.get(`${API}/users/me/email-prefs`, { withCredentials: true })
            .then(({ data }) => setPrefs(data))
            .catch(() => setPrefs(null));

        // Recently opened — quiet relationship layer.
        getMyCardOpens(20)
            .then((items) => setOpens(items))
            .catch(() => setOpens([]));

        // try admin endpoint only if user is admin
        if (user?.is_admin) {
            axios.get(`${API}/admin/referrals/analytics`, { withCredentials: true })
                .then(({ data }) => setAdmin(data))
                .catch(() => setAdmin(null));
            axios.get(`${API}/admin/email-status`, { withCredentials: true })
                .then(({ data }) => setEmailStatus(data))
                .catch(() => setEmailStatus(null));
            // iter-79: spice-level analytics — visible at a glance which voice
            // register users are actually picking. Default window: 30 days.
            axios.get(`${API}/admin/spice-stats?days=30`, { withCredentials: true })
                .then(({ data }) => setSpiceStats(data))
                .catch(() => setSpiceStats(null));
        }
    }, [user, loading, navigate]);

    if (loading || !user) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-app">
                <span className="font-sans text-xs uppercase tracking-[0.3em] text-app-3">loading…</span>
            </div>
        );
    }

    const handleCopy = async () => {
        if (!my?.share_url) return;
        try {
            await navigator.clipboard.writeText(canonicalShareUrl(my.share_url));
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
            toast.success("Link copied");
        } catch { /* ignore */ }
    };

    const handleShare = async () => {
        if (!my?.share_url) return;
        if (!navigator.share) return handleCopy();
        try {
            await navigator.share({
                title: "MoodCard — say it once, say it beautifully.",
                text: "Turn how you feel into a beautiful card you can post anywhere.",
                url: canonicalShareUrl(my.share_url),
            });
        } catch { /* user cancelled */ }
    };

    const togglePulse = async () => {
        if (!prefs) return;
        const next = !prefs.weekly_pulse;
        setPrefs({ ...prefs, weekly_pulse: next });
        try {
            await axios.post(`${API}/users/me/email-prefs`, { weekly_pulse: next }, { withCredentials: true });
            toast.success(next ? "Weekly pulse on" : "Weekly pulse paused");
        } catch {
            setPrefs({ ...prefs, weekly_pulse: !next });
        }
    };

    const sendNow = async () => {
        if (!prefs?.email_enabled) {
            toast.error("Email isn't configured yet — add RESEND_API_KEY to enable.");
            return;
        }
        setSendingPulse(true);
        try {
            const { data } = await axios.post(`${API}/users/me/send-pulse`, {}, { withCredentials: true });
            if (data.ok) toast.success("Pulse sent", { description: `Check ${prefs.email}` });
            else toast.error("Send failed", { description: data.error || "Try again later." });
        } catch (e) {
            toast.error("Send failed");
        } finally {
            setSendingPulse(false);
        }
    };

    const totalMy = my?.total_referrals || 0;
    const maxMy = Math.max(1, ...Object.values(my?.by_pack || {}));
    const maxAdmin = Math.max(1, ...(admin?.by_pack || []).map((b) => b.count));

    return (
        <div className="relative min-h-screen w-full bg-app overflow-x-hidden" data-testid="dashboard-screen">
            <div className="absolute inset-0 aurora-wash opacity-30 pointer-events-none" />
            <div className="absolute inset-0 grain pointer-events-none" />
            <Toaster theme="dark" position="bottom-center" />

            <header className="relative z-10 max-w-md mx-auto px-6 pt-6 flex items-center justify-between">
                <button onClick={() => navigate("/")} className="flex items-center gap-2 text-app-2 hover:text-app transition-colors" data-testid="dashboard-back">
                    <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                    <span className="font-sans text-xs uppercase tracking-[0.3em]">home</span>
                </button>
                <div className="flex items-center gap-2">
                    <ThemeToggle />
                    <AuthButton compact />
                </div>
            </header>

            <div className="relative z-10 max-w-md mx-auto px-6 pt-8 pb-24 space-y-8">
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                    <p className="font-sans text-[10px] uppercase tracking-[0.4em] text-app-3 mb-3">
                        your dashboard
                    </p>
                    <h2 className="font-serif font-light text-app text-3xl sm:text-4xl leading-tight tracking-tight">
                        a private <span className="italic text-app-2">view of you.</span>
                    </h2>
                </motion.div>

                {/* Recently opened — soft micro-reply loop. Renders the
                    panel for both states:
                    - opens.length > 0 → the list
                    - opens loaded but empty → quiet empty-state copy
                    Skips entirely while loading (opens === null) so we
                    don't flash a "no opens" line before data arrives. */}
                {opens && opens.length > 0 && (
                    <section className="rounded-2xl border border-app/20 bg-surface-1 p-5" data-testid="dashboard-opens">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <Inbox className="w-3.5 h-3.5 text-app-2" strokeWidth={1.5} />
                                <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-app-3">
                                    quietly opened
                                </span>
                            </div>
                            <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-app-3">
                                {opens.length}
                            </span>
                        </div>
                        <ul className="space-y-3.5">
                            {opens.slice(0, 8).map((o) => (
                                <li
                                    key={o.open_id}
                                    data-testid={`dashboard-open-${o.open_id}`}
                                    className="group flex items-start gap-3 cursor-pointer"
                                    onClick={() => o.slug && navigate(`/open/${o.slug}`)}
                                >
                                    {/* Tiny amber pulse — same language as the SleeveIntro */}
                                    <span
                                        className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                                        style={{ background: "#d4af6e", boxShadow: "0 0 8px rgba(212,175,110,0.45)" }}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-serif italic text-app text-[15px] leading-snug">
                                            {o.recipient ? (
                                                <>
                                                    <span className="not-italic font-serif">{o.recipient}</span>
                                                    <span className="text-app-2"> opened your card</span>
                                                </>
                                            ) : (
                                                <span className="text-app-2">Someone opened your card</span>
                                            )}
                                        </p>
                                        {o.feeling && (
                                            <p className="mt-0.5 font-sans text-[11px] text-app-3 truncate">
                                                "{o.feeling.length > 64 ? o.feeling.slice(0, 64) + "…" : o.feeling}"
                                            </p>
                                        )}
                                        {/* Thread affordance — only when this card sits in a
                                            chain of >=3. "View thread" reads naturally as an
                                            extension of "they opened it". Stops propagation
                                            so the row's primary onClick (open card) still
                                            works for the rest of the row. */}
                                        {o.thread_count >= 3 && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); navigate(`/thread/${o.mood_id}`); }}
                                                data-testid={`dashboard-open-thread-${o.open_id}`}
                                                className="mt-1.5 inline-flex items-center gap-1.5 font-sans text-[9px] uppercase tracking-[0.3em] text-amber-200/70 hover:text-amber-100 transition-colors"
                                            >
                                                <span className="h-[3px] w-[3px] rounded-full bg-current opacity-80" />
                                                view thread · {o.thread_count}
                                            </button>
                                        )}
                                    </div>
                                    <span className="font-sans text-[10px] uppercase tracking-[0.22em] text-app-3 shrink-0">
                                        {_timeAgo(o.last_opened_at || o.opened_at)}
                                    </span>
                                </li>
                            ))}
                        </ul>
                        <p className="mt-4 font-serif italic text-[11px] text-app-3 opacity-80">
                            we never see what they said back — only that they listened.
                        </p>
                    </section>
                )}

                {/* Empty state — replaces the silent "nothing here" feel.
                    Quiet italic copy + soft amber pulse-dot matches the
                    visual language of populated opens. Encourages the
                    user to make their first card without nagging. */}
                {opens && opens.length === 0 && (
                    <section
                        className="rounded-2xl border border-app/15 bg-surface-1/60 p-6 text-center"
                        data-testid="dashboard-opens-empty"
                    >
                        <div className="inline-flex items-center gap-2 mb-3">
                            <span
                                className="h-1.5 w-1.5 rounded-full"
                                style={{ background: "#d4af6e", opacity: 0.45 }}
                            />
                            <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-app-3">
                                quietly opened
                            </span>
                        </div>
                        <p className="font-serif italic text-app-2 text-[15px] leading-relaxed max-w-xs mx-auto">
                            no one has opened a card yet — make one and send it to someone to begin.
                        </p>
                        <button
                            onClick={() => navigate("/")}
                            data-testid="dashboard-opens-empty-cta"
                            className="btn-press mt-5 inline-flex items-center gap-2 font-sans text-[10px] uppercase tracking-[0.32em] text-amber-200/75 hover:text-amber-100 transition-colors"
                        >
                            <span className="h-1 w-1 rounded-full" style={{ background: "#d4af6e" }} />
                            make your first
                        </button>
                    </section>
                )}

                {/* Signature backdrop — Premium creator's default card photo. */}
                <SignatureBackdrop
                    isPremium={!!isPremium}
                    onUpsell={() => navigate("/?premium=1")}
                />

                {/* Creator Studio — Premium identity that follows every new card. */}
                <CreatorStudio
                    isPremium={!!isPremium}
                    onUpsell={() => navigate("/?premium=1")}
                />

                {/* Referral analytics */}
                <section className="rounded-2xl border border-app/20 bg-surface-1 p-5" data-testid="my-referrals">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <Gift className="w-3.5 h-3.5 text-app-2" strokeWidth={1.5} />
                            <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-app-3">
                                your referrals
                            </span>
                        </div>
                        {my?.credits_unlocked && (
                            <span className="flex items-center gap-1 font-sans text-[10px] uppercase tracking-[0.3em] text-app">
                                <Crown className="w-3 h-3" strokeWidth={1.5} /> unlocked
                            </span>
                        )}
                    </div>

                    <div className="flex items-baseline gap-2 mb-1">
                        <span className="font-serif text-app text-4xl tracking-tight">{totalMy}</span>
                        <span className="font-sans text-xs text-app-3">/ {my?.threshold || 3} to unlock</span>
                    </div>
                    <p className="font-serif italic text-app-2 text-sm mb-4">
                        {totalMy >= (my?.threshold || 3)
                            ? "you earned premium by sharing. thank you."
                            : `share with friends — ${Math.max(0, (my?.threshold || 3) - totalMy)} more to unlock premium.`}
                    </p>

                    {totalMy > 0 && (
                        <div className="space-y-2 mb-5">
                            <div className="font-sans text-[10px] uppercase tracking-[0.3em] text-app-3 mb-2">
                                by pack — which aesthetics they pick
                            </div>
                            {Object.entries(my.by_pack).length === 0 ? (
                                <p className="font-sans text-xs text-app-3">No pack data yet.</p>
                            ) : (
                                Object.entries(my.by_pack).map(([k, v]) => (
                                    <Bar key={k} label={PACK_LABELS[k] || k} count={v} max={maxMy} color={PACK_COLORS[k] || "#8A857E"} />
                                ))
                            )}
                        </div>
                    )}

                    {my?.share_url && (
                        <div className="flex gap-2">
                            <button onClick={handleCopy} data-testid="dashboard-referral-copy"
                                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full border border-app/30 bg-surface-2 text-app font-sans text-xs hover:bg-surface-3 transition-colors">
                                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                                {copied ? "Copied" : "Copy link"}
                            </button>
                            <button onClick={handleShare} data-testid="dashboard-referral-share"
                                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full bg-cta text-app-inverse font-sans text-xs hover:opacity-90 transition-opacity">
                                <Share2 className="w-3.5 h-3.5" /> Share
                            </button>
                        </div>
                    )}
                </section>

                {/* Email preferences */}
                {prefs && (
                    <section className="rounded-2xl border border-app/20 bg-surface-1 p-5" data-testid="email-prefs">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                {prefs.weekly_pulse ? <Mail className="w-3.5 h-3.5 text-app-2" strokeWidth={1.5} /> : <MailX className="w-3.5 h-3.5 text-app-2" strokeWidth={1.5} />}
                                <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-app-3">mood pulse</span>
                            </div>
                            <button
                                onClick={togglePulse}
                                data-testid="pulse-toggle"
                                aria-pressed={prefs.weekly_pulse}
                                className="relative h-5 w-9 rounded-full transition-colors"
                                style={{ backgroundColor: prefs.weekly_pulse ? "var(--app-text)" : "var(--surface-3, rgba(255,255,255,0.09))" }}
                            >
                                <span
                                    className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full transition-transform ${prefs.weekly_pulse ? "translate-x-4" : ""}`}
                                    style={{ backgroundColor: prefs.weekly_pulse ? "var(--app-bg)" : "var(--app-text)" }}
                                />
                            </button>
                        </div>
                        <p className="font-serif italic text-app-2 text-sm mb-4">
                            Every Sunday at 9am, a quiet gallery of your week.
                            {prefs.email && <> Delivered to <span className="text-app">{prefs.email}</span>.</>}
                        </p>
                        <button
                            onClick={sendNow}
                            disabled={sendingPulse || !prefs.email_enabled}
                            data-testid="send-pulse-now"
                            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full border border-app/30 bg-surface-2 text-app font-sans text-xs hover:bg-surface-3 transition-colors disabled:opacity-50"
                        >
                            <Send className="w-3.5 h-3.5" strokeWidth={1.5} />
                            {sendingPulse ? "Sending…" : prefs.email_enabled ? "Send me one now" : "Email not configured yet"}
                        </button>
                    </section>
                )}

                {/* iter-79: Spice level analytics tile (admin only). */}
                {spiceStats && (
                    <section className="rounded-2xl border border-app/20 bg-surface-1 p-5" data-testid="admin-spice-stats">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <BarChart3 className="w-3.5 h-3.5 text-app-2" strokeWidth={1.5} />
                                <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-app-3">spice mix · {spiceStats.window_days}d</span>
                            </div>
                            <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-app-3">
                                {spiceStats.total} cards
                            </span>
                        </div>
                        {spiceStats.total === 0 ? (
                            <p className="font-serif italic text-app-3 text-sm">no cards yet in this window.</p>
                        ) : (
                            <>
                                <div className="space-y-2.5">
                                    {["unhinged", "chaotic", "bold", "mild", "default"].map((key) => {
                                        const count = spiceStats.by_spice[key] || 0;
                                        if (count === 0) return null;
                                        const pct = spiceStats.percentages[key] || 0;
                                        const color =
                                            key === "unhinged" ? "#fb7185" :
                                            key === "chaotic"  ? "#f97316" :
                                            key === "bold"     ? "#fbbf24" :
                                            key === "mild"     ? "#a3e635" :
                                                                "#94a3b8";
                                        return (
                                            <div key={key} data-testid={`spice-row-${key}`}>
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="font-sans text-[11px] uppercase tracking-[0.18em] text-app-2">{key}</span>
                                                    <span className="font-sans text-[11px] text-app-3">
                                                        <span className="text-app-2 font-medium">{count}</span> · {pct}%
                                                    </span>
                                                </div>
                                                <div className="h-1.5 rounded-full bg-surface-2 overflow-hidden">
                                                    <div
                                                        className="h-full rounded-full"
                                                        style={{ width: `${pct}%`, backgroundColor: color }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <SpiceSparkline daily={spiceStats.daily} windowDays={spiceStats.daily_window_days} />
                            </>
                        )}
                    </section>
                )}

                {/* Admin dashboard */}
                {admin && (
                    <section className="rounded-2xl border border-app/20 bg-surface-1 p-5" data-testid="admin-analytics">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <BarChart3 className="w-3.5 h-3.5 text-app-2" strokeWidth={1.5} />
                                <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-app-3">global · admin</span>
                            </div>
                            <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-app-3">
                                {admin.total_referral_events} events
                            </span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 mb-5">
                            {[
                                { k: "total_codes", label: "codes" },
                                { k: "credits_unlocked_count", label: "unlocked" },
                                { k: "total_referral_events", label: "referrals" },
                            ].map((s) => (
                                <div key={s.k} className="rounded-xl border border-app/20 bg-surface-2 p-3 text-center">
                                    <div className="font-serif text-app text-2xl">{admin[s.k] ?? 0}</div>
                                    <div className="font-sans text-[10px] uppercase tracking-[0.25em] text-app-3 mt-1">{s.label}</div>
                                </div>
                            ))}
                        </div>

                        <div className="space-y-2 mb-5">
                            <div className="font-sans text-[10px] uppercase tracking-[0.3em] text-app-3 mb-1">
                                conversion by pack
                            </div>
                            {admin.by_pack.length === 0 ? (
                                <p className="font-sans text-xs text-app-3">No referrals yet.</p>
                            ) : (
                                admin.by_pack.map((b) => (
                                    <Bar key={b.pack} label={PACK_LABELS[b.pack] || b.pack} count={b.count} max={maxAdmin} color={PACK_COLORS[b.pack] || "#8A857E"} />
                                ))
                            )}
                        </div>

                        {admin.top_referrers.length > 0 && (
                            <div>
                                <div className="font-sans text-[10px] uppercase tracking-[0.3em] text-app-3 mb-2">
                                    top referrers
                                </div>
                                <ul className="space-y-1">
                                    {admin.top_referrers.slice(0, 5).map((t) => (
                                        <li key={t.code} className="flex items-center justify-between font-sans text-xs text-app-2">
                                            <span className="font-mono text-app">{t.code}</span>
                                            <span className="text-app-3">{t.count}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </section>
                )}
                {/* Admin email status */}
                {emailStatus && (
                    <section className="rounded-2xl border border-app/20 bg-surface-1 p-5" data-testid="email-status">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <Mail className="w-3.5 h-3.5 text-app-2" strokeWidth={1.5} />
                                <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-app-3">
                                    email · resend · admin
                                </span>
                            </div>
                            <span
                                className="font-sans text-[10px] uppercase tracking-[0.3em]"
                                style={{ color: emailStatus.domain_status === "verified" ? "#9FE7D9" : "#F2B49B" }}
                            >
                                {emailStatus.domain_status || (emailStatus.email_enabled ? "no-domain" : "no-key")}
                            </span>
                        </div>
                        <p className="font-serif italic text-app-2 text-sm mb-3">
                            Sending from <span className="font-mono not-italic text-app">{emailStatus.sender || "—"}</span>.
                        </p>
                        {emailStatus.domains.length > 0 ? (
                            <ul className="space-y-1">
                                {emailStatus.domains.map((d) => (
                                    <li key={d.name} className="flex items-center justify-between font-sans text-xs">
                                        <span className="font-mono text-app">{d.name}</span>
                                        <span className="text-app-3">{d.status}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="font-sans text-xs text-app-3">
                                {emailStatus.email_enabled
                                    ? "No domains added in Resend yet. Add moodcard.app at https://resend.com/domains."
                                    : "Add RESEND_API_KEY to .env to enable email."}
                            </p>
                        )}
                        {emailStatus.error && (
                            <p className="mt-3 font-sans text-[11px] text-app-3 break-words">
                                Last error: {emailStatus.error}
                            </p>
                        )}
                    </section>
                )}
            </div>
        </div>
    );
}

/**
 * SpiceSparkline — tiny stacked daily breakdown chart for the analytics tile.
 *
 * iter-80: takes the `daily` rows from /admin/spice-stats and pivots them
 * into a one-line-per-spice mini chart. Lines colored consistently with the
 * bar rows above.
 */
function SpiceSparkline({ daily, windowDays }) {
    const data = useMemo(() => {
        const byDay = new Map();
        (daily || []).forEach((row) => {
            const day = row.day;
            if (!byDay.has(day)) byDay.set(day, { day });
            byDay.get(day)[row.spice] = (byDay.get(day)[row.spice] || 0) + row.count;
        });
        return Array.from(byDay.values()).sort((a, b) => a.day.localeCompare(b.day));
    }, [daily]);

    if (!data.length) return null;

    return (
        <div className="mt-5" data-testid="spice-sparkline">
            <div className="text-[10px] uppercase tracking-[0.22em] text-app-3 mb-2">
                last {windowDays}d trend
            </div>
            <div className="h-24">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                        <RTooltip
                            cursor={{ stroke: "rgba(255,255,255,0.08)" }}
                            contentStyle={{
                                background: "#0f0f12",
                                border: "1px solid rgba(255,255,255,0.12)",
                                borderRadius: 8,
                                fontSize: 11,
                            }}
                            labelStyle={{ color: "rgba(255,255,255,0.55)" }}
                        />
                        <Line type="monotone" dataKey="unhinged" stroke="#fb7185" strokeWidth={1.5} dot={false} />
                        <Line type="monotone" dataKey="chaotic"  stroke="#f97316" strokeWidth={1.5} dot={false} />
                        <Line type="monotone" dataKey="bold"     stroke="#fbbf24" strokeWidth={1.5} dot={false} />
                        <Line type="monotone" dataKey="mild"     stroke="#a3e635" strokeWidth={1.5} dot={false} />
                        <Line type="monotone" dataKey="default"  stroke="#94a3b8" strokeWidth={1.2} dot={false} strokeDasharray="2 2" />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

